// Nachschlagen von BIC und Kreditinstitut zu einer deutschen Bankleitzahl.
// Datenbasis: apps/web/src/data/bankleitzahlen.json, erzeugt aus der
// Bankleitzahlendatei der Deutschen Bundesbank
// (apps/web/scripts/generate-bankleitzahlen.mjs) — Quelle: Deutsche Bundesbank.
//
// Zwei Entscheidungen, die man beim Lesen sonst hinterfragt:
//
// 1. Die Daten liegen im Frontend, nicht im Backend. Damit verlässt die IBAN
//    beim Nachschlagen nie den Browser. Ein Endpunkt, der eine IBAN im Pfad
//    oder Query-String entgegennimmt, schriebe Kundendaten in jedes
//    nginx-Access-Log — eine Bankleitzahl allein ist kein personenbezogenes
//    Datum, eine IBAN sehr wohl. Dasselbe Muster nutzt bereits die
//    Finanzamt-Auswahl (finanzaemter.ts).
//
// 2. Geladen wird per dynamischem Import, also erst wenn tatsächlich jemand
//    eine IBAN eintippt. Die Datei ist ~220 KB (~49 KB gzip) und gehört nicht
//    in den Start-Bundle einer Maske, die meistens gar keine Bankdaten zeigt.

export interface BankTreffer {
  /** Kurzbezeichnung des Instituts, wie sie in ein Formularfeld passt. */
  bankName: string;
  /** 11-stelliger BIC. Fehlt bei 2 von rund 3.500 Instituten. */
  bic?: string;
  /** Die Bundesbank hat die Löschung dieser Bankleitzahl angekündigt. */
  endet?: boolean;
}

interface Verzeichnis {
  stand: string;
  banken: Record<string, { name: string; bic?: string; endet?: boolean }>;
}

let geladen: Promise<Verzeichnis> | null = null;

/** Lädt das Verzeichnis einmalig und merkt sich das Versprechen. */
function verzeichnis(): Promise<Verzeichnis> {
  if (!geladen) {
    geladen = import("../../../data/bankleitzahlen.json").then(
      (m) => (m.default ?? m) as unknown as Verzeichnis,
    );
  }
  return geladen;
}

/**
 * BIC und Institutsname zu einer deutschen Bankleitzahl, oder `undefined`.
 *
 * Ein Nicht-Treffer ist ausdrücklich kein Fehler: Er darf weder die
 * IBAN-Prüfung beeinflussen noch das Speichern blockieren. Über die
 * Gültigkeit einer IBAN entscheidet allein die Prüfziffer (mod 97).
 */
export async function bankZuBankleitzahl(blz: string): Promise<BankTreffer | undefined> {
  if (!/^\d{8}$/.test(blz)) return undefined;
  try {
    const { banken } = await verzeichnis();
    const eintrag = banken[blz];
    if (!eintrag) return undefined;
    return { bankName: eintrag.name, bic: eintrag.bic, endet: eintrag.endet };
  } catch {
    // Verzeichnis nicht ladbar (Netz, Chunk-Fehler) — dann gibt es eben kein
    // Auto-Fill. Die Maske bleibt bedienbar.
    return undefined;
  }
}

/** Datenstand des Verzeichnisses (ISO-Datum) — für Hinweise in der Oberfläche. */
export async function bankverzeichnisStand(): Promise<string | undefined> {
  try {
    return (await verzeichnis()).stand;
  } catch {
    return undefined;
  }
}
